import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'
import { DotLoader } from '@/components/ui/DotLoader'

// Canonical brand CTA per design-aesthetics §3 (brand bg constant across modes) + §7
// (primary CTA hover → #7170ff, active → #5058c0). §53 canonical control metrics:
// every size is h-9 (36px) with rounded-md (6px), declared once in the base string.
const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 active:scale-[0.97]',
  {
    variants: {
      variant: {
        default: 'bg-[#5e6ad2] text-white shadow-sm hover:bg-[#7170ff] active:bg-[#5058c0]',
        destructive: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline: 'border border-border/60 bg-background text-foreground hover:bg-muted/50 hover:border-border/80 dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#e2e4e7] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.14]',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'text-foreground hover:bg-muted/50 dark:text-[#d0d6e0] dark:hover:bg-white/[0.04]',
        link: 'text-[#5e6ad2] dark:text-[#a5b4fc] underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-10 px-6',
        xl: 'h-11 px-8 text-base',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean
  icon?: React.ReactNode
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, icon, children, disabled, onClick, ...props }, ref) => {
    // A *pending* control is not an *inert* control (§55). Native `disabled`
    // takes the button out of the tab order and out of screen readers'
    // actionable-element navigation, so `aria-busy` on a natively-disabled
    // button announces to a control the user can no longer reach. While
    // `loading` we therefore stay natively enabled — focusable and announced —
    // and express the refusal with `aria-disabled` + the click guard below.
    // Native `disabled` is reserved for the `disabled` prop: a genuinely inert
    // control stays genuinely inert.
    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        if (loading) {
          // Load-bearing: without pointer-events-none a loading button is
          // still clickable, and a second click would double-submit. Also
          // covers implicit form submission, which dispatches a click here.
          e.preventDefault()
          e.stopPropagation()
          return
        }
        onClick?.(e)
      },
      [loading, onClick],
    )

    return (
      <button
        className={cn(
          buttonVariants({ variant, size }),
          // The base string's `disabled:` utilities no longer fire while
          // loading (the control is aria-disabled, not disabled), so mirror
          // the non-interactive look here. Deliberately NOT
          // `pointer-events-none` — that would kill the focus and hover this
          // whole pattern exists to preserve. handleClick is the enforcement.
          loading && 'opacity-50 cursor-wait active:scale-100',
          className,
        )}
        ref={ref}
        disabled={disabled}
        {...props}
        aria-busy={loading || undefined}
        aria-disabled={loading || undefined}
        onClick={handleClick}
      >
        {loading && <DotLoader size="sm" announce={false} className="mr-2" />}
        {!loading && icon && <span className="mr-2">{icon}</span>}
        {children}
      </button>
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
